/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  CreatorRole,
  GetChangeDto,
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessListForChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import LoadSpinnerSmall from '../LoadSpinner/LoadSpinnerSmall';
import {
  formatNumber,
  getSumOfMaterialAndService,
} from '../../utils/getTotalTime';
import MaterialDataGrid from './MaterialDataGrid';
import { useMaterialColumns } from '../../utils/tableColumns';
import ProcessItemInfo from '../ProcessItems/ProcessItemInfo';
import {
  useWorkMaterialApiFactory,
  useWorkServiceApiFactory,
} from '../../ApiClient';
import { useChangeStore } from '../../store/changeStore';
import { WorkItemWithType } from '../../types/workPackages';
import ProcessAccordionTitle from '../ProcessItems/ProcessAccordionTitle';
import { getDepartmentById } from '../../utils/utils';
import { useUserHasFullAccess } from '../../utils/permissionHelper';

type ProcessCardProps = {
  process: ProcessListForChangeDto;
  workMaterials: ListWorkMaterialDto[] | [];
  services: ListWorkServiceDto[];
  change: GetChangeDto | null;
  handleChangeProcess: (fieldName: string, value: number, id: number) => void;
  handleActiveProcess: (id: number) => void;
  editable: boolean;
  isActive: boolean;
  workPackages: ListWorkPackageDto[];
  isLoading: boolean;
  handleMaterialModal: (id?: number | undefined) => void;
  handleServiceModal: (id?: number | undefined) => void;
};

const MaterialTable = observer(
  ({
    process,
    workMaterials,
    services,
    change,
    workPackages,
    isActive,
    isLoading,
    handleServiceModal,
    handleMaterialModal,
  }: ProcessCardProps) => {
    const { t } = useTranslation();
    const { calculatedHours, timeImpact, releasedHours } = process;

    const { removeServicePackage, removeMaterialPackage } = useChangeStore();

    const workServicesApi = useWorkServiceApiFactory();
    const workMaterialsApi = useWorkMaterialApiFactory();
    const userHasFullAccess = useUserHasFullAccess(change);

    const department = getDepartmentById(
      process?.pId ?? 0,
      change?.commonProject?.navProcessToCommonProjectRelations,
    );

    const mapWithType = <T extends WorkItemWithType>(
      arr: T[],
      type: 'service' | 'material',
    ) => arr.map(item => ({ ...item, type }));

    // Technician Data
    const technicianData: WorkItemWithType[] = [
      ...mapWithType(
        services.filter(s => s.creatorRole === CreatorRole.CoreTeamMember),
        'service',
      ),
      ...mapWithType(
        workMaterials.filter(m => m.creatorRole === CreatorRole.CoreTeamMember),
        'material',
      ),
    ];

    // Calculation Data
    const calculationData: WorkItemWithType[] = [
      ...mapWithType(
        services.filter(
          s => s.creatorRole === CreatorRole.CalculationResponsible,
        ),
        'service',
      ),
      ...mapWithType(
        workMaterials.filter(
          m => m.creatorRole === CreatorRole.CalculationResponsible,
        ),
        'material',
      ),
    ];

    // Boss Data
    const bossData: WorkItemWithType[] = [
      ...mapWithType(
        services.filter(s => s.creatorRole === CreatorRole.ChangeManager),
        'service',
      ),
      ...mapWithType(
        workMaterials.filter(m => m.creatorRole === CreatorRole.ChangeManager),
        'material',
      ),
    ];

    const isEditableCalc =
      change?.status === UpdateChangeDtoStatusEnum.EvalAccepted;

    const isEditableCM =
      change?.status === UpdateChangeDtoStatusEnum.CalcComplete ||
      change?.status ===
        UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved ||
      change?.status === UpdateChangeDtoStatusEnum.InternalApprovalAccepted;

    const showSalesGroup =
      change?.status ===
        UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved ||
      change?.status === UpdateChangeDtoStatusEnum.InternalApprovalAccepted ||
      change?.status === UpdateChangeDtoStatusEnum.Check ||
      change?.status ===
        UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
      change?.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
      change?.status === UpdateChangeDtoStatusEnum.Rejected ||
      change?.status === UpdateChangeDtoStatusEnum.InternalApprovalRejected;

    const handleEditEntry = (params: GridRowParams<WorkItemWithType>) => {
      if (params.row.type === 'service') {
        handleServiceModal(params.row.id);
      } else if (params.row.type === 'material') {
        handleMaterialModal(params.row.id);
      }
    };

    const handleDeleteEntry = async (
      params: GridRowParams<WorkItemWithType>,
    ) => {
      try {
        if (params.row.type === 'service') {
          await workServicesApi.workServicesControllerRemove({
            id: params.row.id,
          });
          removeServicePackage(params.row.id);
        } else {
          await workMaterialsApi.workMaterialsControllerRemove({
            id: params.row.id,
          });
          removeMaterialPackage(params.row.id);
        }
      } catch (e: any) {
        console.log(e);
      }
    };

    const materialColumns = useMaterialColumns();
    const editableColumns: GridColDef<WorkItemWithType>[] = [
      ...materialColumns.filter(col => col.field !== 'actions'),
      {
        field: 'actions',
        type: 'actions',
        headerName: t('buttons.actions'),
        width: 80,
        getActions: params => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditEntry(params)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteEntry(params)}
            color="inherit"
          />,
        ],
      },
    ];

    return (
      <Accordion
        css={css`
          width: 100%;
          background-color: white;
          margin-bottom: 16px;

          &::before {
            display: none;
          }
          & .MuiAccordionSummary-gutters {
            padding-left: 10px;
          }
          &.Mui-expanded {
            margin: 0;
            margin-bottom: 16px;
          }
          border: 1px solid #e2e8f0;
        `}
      >
        <AccordionSummary
          aria-controls="panel1-content"
          id="panel1-header"
          expandIcon={
            <ExpandMoreIcon
              color="primary"
              css={css`
                transform: rotate(-90deg);
              `}
            />
          }
          css={css`
            flex-direction: row-reverse;
            gap: 16px;
            height: 70px;
            align-items: center;

            & .MuiAccordionSummary-content ~ .Mui-expanded {
              transform: rotate(90deg) !important;
            }
          `}
        >
          {department && <ProcessAccordionTitle department={department} />}
          <ProcessItemInfo
            workPackages={workPackages}
            calculatedHours={calculatedHours}
            releasedHours={releasedHours}
            timeImpact={timeImpact}
            process={process}
            workServices={services}
            workMaterials={workMaterials}
            showCalculation
            showImplementation={false}
            userHasFullAccess={userHasFullAccess}
          />
        </AccordionSummary>

        <AccordionDetails>
          {isLoading && isActive && <LoadSpinnerSmall />}
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              background-color: white;
              gap: 16px;
            `}
          >
            <Box>
              {/* Technician Section */}
              <MaterialDataGrid
                rows={technicianData}
                columns={materialColumns}
                showType
                title={t('calc.ctmCostsTableHeading')}
                handleServiceModal={handleServiceModal}
                handleMaterialModal={handleMaterialModal}
              />
              <Box
                css={css`
                  background-color: #f0f4f7;
                  padding: 16px 8px 16px 16px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <Typography
                  variant="h5"
                  css={css`
                    font-size: 0.875rem;
                    font-weight: 700;
                    color: #333;
                  `}
                >
                  {t('calc.ctmCostsTableSum')}
                </Typography>
                <Typography
                  variant="h5"
                  css={css`
                    font-size: 0.875rem;
                    font-weight: 700;
                    color: #333;
                  `}
                >
                  {formatNumber(getSumOfMaterialAndService(technicianData))}
                </Typography>
              </Box>
            </Box>
            <Box>
              {/* Calculation Section */}
              <MaterialDataGrid
                rows={calculationData}
                columns={isEditableCalc ? editableColumns : materialColumns}
                title={t('calc.calcCostsTableHeading')}
                isEditable={isEditableCalc}
                showType
                handleServiceModal={handleServiceModal}
                handleMaterialModal={handleMaterialModal}
              />
              <Box
                css={css`
                  background-color: #f0f4f7;
                  padding: 16px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <Typography
                  variant="h5"
                  css={css`
                    font-size: 0.875rem;
                    font-weight: 700;
                    color: #333;
                  `}
                >
                  {t('calc.calcCostsTableSum')}
                </Typography>
                <Typography
                  variant="h5"
                  css={css`
                    font-weight: 700;
                    color: #333;
                    font-size: 0.875rem;
                  `}
                  sx={{ marginRight: isEditableCalc ? '75px' : 0 }}
                >
                  {formatNumber(getSumOfMaterialAndService(calculationData))}
                </Typography>
              </Box>
            </Box>
            <Box>
              {showSalesGroup && (
                <Box>
                  {/* Boss Section */}
                  <MaterialDataGrid
                    rows={bossData}
                    columns={isEditableCM ? editableColumns : materialColumns}
                    title={t('calc.cmCostsTableHeading')}
                    isEditable={isEditableCM}
                    showType
                    handleServiceModal={handleServiceModal}
                    handleMaterialModal={handleMaterialModal}
                  />
                  <Box
                    css={css`
                      background-color: #f0f4f7;
                      padding: 16px;
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                    `}
                  >
                    <Typography
                      variant="h5"
                      css={css`
                        font-weight: 700;
                        color: #333;
                        font-size: 0.875rem;
                      `}
                    >
                      {t('calc.cmCostsTableSum')}
                    </Typography>
                    <Typography
                      variant="h5"
                      css={css`
                        font-weight: 700;
                        color: #333;
                        font-size: 0.875rem;
                      `}
                      sx={{ marginRight: isEditableCM ? '75px' : 0 }}
                    >
                      {formatNumber(getSumOfMaterialAndService(bossData))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default MaterialTable;
